import { useState } from 'react';
import { Button } from '@icetus/ui';
import { ApiError, resetPassword } from '../api';
import { Brand } from '../components/brand';

/**
 * 重置密码落地页（`/reset/<token>`）。
 *
 * 与邀请落地页同一个位置：都在登录门禁**之前**。客户是从邮件点进来的，
 * 那一刻他多半没有会话 —— 而他要做的恰恰是重新拿到登录资格。
 *
 * 三个细节：
 *
 * 1) **页面加载不消费令牌**，只有提交才消费。这一点比"验证邮箱"那条链路安全：
 *    企业邮箱和网关会预取正文里的链接，GET 就作废的话，客户还没点链接就失效了。
 * 2) **失败不覆盖地址栏**。令牌无效时留在这一页，让客户能看清为什么，
 *    再点"重新申请一次"。直接跳回登录页会让这次点击看起来毫无效果。
 * 3) 密码规则只在服务端判定，前端只做"两次一致"和一条长度提示。
 *    在这里复制一份规则，早晚会和 `passwordProblem` 漂移成两套。
 */
export function ResetGate({ token, onBackToLogin }: { token: string; onBackToLogin: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  /** 成功后的邮箱，只用来把话说具体 */
  const [done, setDone] = useState<string | null>(null);

  /** 令牌层面的失败：这类错误重试没有意义，只能重新申请 */
  const tokenDead = errorCode === 'invalid_token' || errorCode === 'token_used';

  const submit = async (): Promise<void> => {
    if (busy) return;
    if (password.length < 12) {
      setError('密码至少 12 位');
      setErrorCode(null);
      return;
    }
    if (password !== confirm) {
      setError('两次输入的密码不一致');
      setErrorCode(null);
      return;
    }
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const result = await resetPassword(token, password);
      setDone(result.email);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setErrorCode(err.code);
      } else {
        setError(String(err));
        setErrorCode(null);
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="login">
        <div className="login__card">
          <Brand />
          <h2 className="login__title">密码已更新</h2>
          <p className="muted">
            <strong>{done}</strong> 的密码已经改好了，现在用新密码登录。
          </p>
          {/*
            这句必须写：老会话被服务端清掉了，客户在另一台电脑上开着的控制台
            会突然要求重新登录。不说清楚，他会以为是系统出了问题。
          */}
          <p className="muted">所有设备上的旧登录都已失效，需要重新登录一次。</p>
          <Button variant="primary" onClick={onBackToLogin}>
            去登录
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="login">
      <div className="login__card">
        <Brand />
        <h2 className="login__title">设置新密码</h2>
        <p className="muted">这个链接只能用一次，设置成功后所有设备都需要重新登录。</p>

        {tokenDead ? (
          <>
            <p className="notice notice--warn">{error}</p>
            <Button variant="primary" onClick={onBackToLogin}>
              重新申请一次
            </Button>
          </>
        ) : (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <input
                className="field"
                type="password"
                autoFocus
                autoComplete="new-password"
                placeholder="新密码（至少 12 位）"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <input
                className="field"
                type="password"
                autoComplete="new-password"
                placeholder="再输一次"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <Button variant="primary" type="submit" disabled={busy}>
                {busy ? '保存中…' : '保存新密码'}
              </Button>
            </form>
            {error ? <p className="error">{error}</p> : null}
            <button type="button" className="login__switch" onClick={onBackToLogin}>
              用其他方式登录
            </button>
          </>
        )}
      </div>
    </div>
  );
}
